/**
 * RTK Query endpoints for design pages.
 *
 * Provides a single shared cache for design page metadata and loaded content.
 * SSE-driven cache updates are wired through `wireDesignSSE()` at the
 * planning-page level.
 */
import type { AppDispatch, RootState } from '..';
import { getClientTimeout } from '../../services/clientTimeoutConfig';
import {
  invalidateEditorPrepareCache,
  type DesignPage,
  type DesignPagesResponse,
} from '../../services/designService';
import { eventService } from '../../services/eventService';

import { api } from './api';
import {
  designPageProgressRevision,
  mergeDesignPageContentResponse,
  mergeDesignPagesFromSse,
  mergeDesignPagesPreservingLoadedContent,
} from './designPageCache';
import { DesignRefreshCoordinator } from './designRefreshCoordinator';

export interface DesignPagesQueryArgs {
  projectId: string;
  light?: boolean;
  includePlanned?: boolean;
}

export interface DesignPageQueryArgs {
  projectId: string;
  pageId: string;
  /** Distinct cache entries prevent a newer preview joining an older in-flight read. */
  revision?: string;
}

const DESIGN_PAGES_CACHE_ARGS: Pick<DesignPagesQueryArgs, 'light' | 'includePlanned'> = {
  light: true,
  includePlanned: true,
};

export const designApi = api.injectEndpoints({
  // Endpoint definitions stay together so both queries share one cache contract.
  // eslint-disable-next-line max-lines-per-function
  endpoints: (builder) => ({

    /**
     * Primary query — all consumers share this cache entry per projectId.
     *
     * Light fetch returns metadata only (no compiled_html / jsx_content).
     * Full content is loaded through `getDesignPage` and merged back into this
     * cache entry so the design page list remains the single shared source.
     */
    getDesignPages: builder.query<DesignPagesResponse, DesignPagesQueryArgs>({
      query: ({ projectId, light = true, includePlanned = true }) => {
        const params = new URLSearchParams();

        if (light) params.set('light', 'true');
        if (includePlanned) params.set('include_planned', 'true');

        const qs = params.toString();

        return `/api/v1/projects/${projectId}/design-pages${qs ? `?${qs}` : ''}`;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const light = queryArgs.light !== false;
        const includePlanned = queryArgs.includePlanned !== false;

        return `${endpointName}:${queryArgs.projectId}:light:${light}:planned:${includePlanned}`;
      },
      merge: (currentCache, response) => {
        currentCache.project_id = response.project_id;
        // Credit status is fully recomputed server-side each fetch, so the
        // freshest response always replaces it (including back to empty once
        // the user tops up and nothing is blocked any more).
        currentCache.credit_status = response.credit_status ?? null;
        currentCache.page_inventory = response.page_inventory ?? null;
        currentCache.live_url = response.live_url ?? null;
        currentCache.design_pages = mergeDesignPagesPreservingLoadedContent(
          currentCache.design_pages,
          response.design_pages,
        );
      },
      providesTags: (_result, _err, { projectId }) => [
        { type: 'DesignPage' as const, id: projectId },
      ],
      async onCacheEntryAdded({ projectId }, {
        cacheDataLoaded, cacheEntryRemoved, getCacheEntry, dispatch,
      }) {
        let timer: ReturnType<typeof setInterval> | undefined;

        try {
          await cacheDataLoaded;
          // A shared, metadata-only read recovers missed completion broadcasts,
          // including when the page was reloaded halfway through generation. The
          // period is the admin panel's `client.design_reconcile_poll_ms` when the poll starts.
          timer = setInterval(() => {
            if (!getCacheEntry().data?.design_pages.some((page) => page.generation_in_progress)) return;
            dispatch(api.util.invalidateTags([{ type: 'DesignPage', id: projectId }]));
          }, getClientTimeout('designReconcilePollMs'));
          await cacheEntryRemoved;
        } catch {
          // An unmounted or failed initial query has nothing to reconcile.
        } finally {
          if (timer !== undefined) clearInterval(timer);
        }
      },
    }),

    getDesignPage: builder.query<DesignPage, DesignPageQueryArgs>({
      query: ({ projectId, pageId }) => (
        `/api/v1/projects/${projectId}/design-pages/${pageId}`
      ),
      providesTags: (_result, _err, { projectId, pageId }) => [
        { type: 'DesignPage' as const, id: `${projectId}:${pageId}` },
      ],
      async onQueryStarted({ projectId, pageId, revision }, { dispatch, getState, queryFulfilled }) {
        const requestedPage = designApi.endpoints.getDesignPages.select({
          projectId, ...DESIGN_PAGES_CACHE_ARGS,
        })(getState() as RootState).data?.design_pages.find((page) => page.id === pageId);
        const requestedProgress = requestedPage && designPageProgressRevision(requestedPage);

        try {
          const { data: fullPage } = await queryFulfilled;

          dispatch(
            designApi.util.updateQueryData(
              'getDesignPages',
              { projectId, ...DESIGN_PAGES_CACHE_ARGS },
              (draft) => {
                const idx = draft.design_pages.findIndex((page) => page.id === fullPage.id);

                if (idx >= 0) {
                  draft.design_pages[idx] = mergeDesignPageContentResponse(
                    draft.design_pages[idx],
                    fullPage,
                    revision,
                    requestedProgress,
                  );
                } else {
                  draft.design_pages.push(fullPage);
                }
              },
            ),
          );
        } catch {
          // Fetch errors are surfaced through the page query itself.
        }
      },
    }),

  }),
});

export const {
  useGetDesignPageQuery,
  useGetDesignPagesQuery,
  useLazyGetDesignPageQuery,
  useLazyGetDesignPagesQuery,
} = designApi;

// ─── SSE → Shared Cache Updates ────────────────────────────────────────────
// Project-scoped SSE listener.  It is reference-counted so multiple callers
// can safely wire the same project without installing duplicate listeners.

interface DesignPagesEventPayload {
  type?: string;
  project_id?: string;
  design_pages?: DesignPagesResponse['design_pages'];
  credit_status?: DesignPagesResponse['credit_status'];
  live_url?: DesignPagesResponse['live_url'];
}

interface DesignCompletionEventPayload {
  project_id?: string;
  phase?: string;
  stage?: string;
  active?: boolean;
}

const wiredProjects = new Map<string, { count: number; cleanup: () => void }>();

/** Which pages have a design: the server's page inventory (missing-page count) is stale once this changes. */
const readinessKey = (pages: DesignPagesResponse['design_pages']): string => pages.map((page) => [
  page.page_name, Boolean(page.has_compiled_html || page.has_jsx_content || page.compiled_html),
  Boolean(page.generation_failed), Boolean(page.credit_blocked), Boolean(page.generation_in_progress),
].join(':')).join('|');

/** Returns null when the payload is not this project's page list, else whether page readiness changed. */
function updateDesignPagesFromSse(
  projectId: string,
  dispatch: AppDispatch,
  payload: DesignPagesEventPayload,
): { readinessChanged: boolean } | null {
  if (payload.project_id && payload.project_id !== projectId) return null;
  if (payload.type !== 'design_pages' || !Array.isArray(payload.design_pages)) return null;

  payload.design_pages.forEach((page) => invalidateEditorPrepareCache(
    projectId, page.id, page.version,
  ));
  let readinessChanged = false;

  dispatch(designApi.util.updateQueryData(
    'getDesignPages',
    { projectId, ...DESIGN_PAGES_CACHE_ARGS },
    (draft) => {
      const before = readinessKey(draft.design_pages);

      draft.project_id = projectId;
      // Every design_pages broadcast carries the recomputed credit picture,
      // so an absent block means "nothing is credit-blocked any more".
      draft.credit_status = payload.credit_status ?? null;
      // Every design_pages broadcast carries the live link too (null until the first page is published).
      if (payload.live_url !== undefined) draft.live_url = payload.live_url;
      draft.design_pages = mergeDesignPagesFromSse(draft.design_pages, payload.design_pages || []);
      readinessChanged = Boolean(draft.page_inventory) && readinessKey(draft.design_pages) !== before;
    },
  ));

  return { readinessChanged };
}

function subscribeDesignCompletion(projectId: string, invalidate: () => void): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const requestRefresh = (data: unknown) => {
    const payload = data as DesignCompletionEventPayload;

    if (payload.project_id && payload.project_id !== projectId) return;
    if (!payload.project_id && !eventService.isCurrentProject(projectId)) return;
    if (timer !== undefined) return;
    timer = setTimeout(() => {
      timer = undefined;
      invalidate();
    }, 100);
  };
  const unsubscribers = [
    eventService.subscribe('planning_complete', requestRefresh),
    eventService.subscribe('planning_stage_loader', (data: unknown) => {
      const payload = data as DesignCompletionEventPayload;

      if (payload.stage === 'design_review' && payload.active === false) requestRefresh(data);
    }),
    eventService.subscribe('chat_message', (data: unknown) => {
      const payload = data as DesignCompletionEventPayload;

      if (payload.phase === 'first_page' || payload.phase === 'all_pages') requestRefresh(data);
    }),
  ];

  return () => {
    unsubscribers.forEach((unsubscribe) => unsubscribe());
    if (timer !== undefined) clearTimeout(timer);
  };
}

// The setup and reference-counted teardown intentionally live in one lifecycle function.
// eslint-disable-next-line max-statements, max-lines-per-function
export function wireDesignSSE(
  projectId: string,
  storeRef: { dispatch: AppDispatch },
): () => void {
  eventService.connect(projectId);

  const existing = wiredProjects.get(projectId);

  if (existing) {
    existing.count += 1;

    return () => {
      existing.count -= 1;
      if (existing.count > 0) return;
      existing.cleanup();
      wiredProjects.delete(projectId);
    };
  }

  const invalidate = () => {
    storeRef.dispatch(api.util.invalidateTags([{ type: 'DesignPage', id: projectId }]));
  };
  const refreshCoordinator = new DesignRefreshCoordinator(projectId, invalidate);
  const unsubscribeSse = eventService.subscribe('design_pages', (data: unknown) => {
    const payload = data as DesignPagesEventPayload;

    const update = updateDesignPagesFromSse(projectId, storeRef.dispatch, payload);

    if (!update) return;
    refreshCoordinator.acknowledgeSse();
    if (update.readinessChanged) invalidate();
  });
  const requestRefresh = (event: Event) => refreshCoordinator.request(event);
  // SSE has no replay cursor. Reload metadata after every successful connection
  // so a missed preview or completion event cannot leave the canvas stale.
  const unsubscribeConnected = eventService.subscribe('connected', () => {
    if (eventService.isCurrentProject(projectId)) invalidate();
  });
  const unsubscribeCompletion = subscribeDesignCompletion(projectId, invalidate);
  // Payments can finish while generation is paused, when the design poller
  // is idle. Refresh the computed shortfall on both account and billing events.
  const unsubscribeCredits = eventService.subscribe('credits_update', invalidate);
  const billingEvents = ['credits_update', 'billing_topup_success', 'billing_upgrade_success'];

  billingEvents.forEach((name) => window.addEventListener(name, invalidate));

  window.addEventListener('design_data_refresh_requested', requestRefresh);
  const cleanup = () => {
    refreshCoordinator.stop();
    unsubscribeSse();
    unsubscribeConnected();
    unsubscribeCompletion();
    unsubscribeCredits();
    billingEvents.forEach((name) => window.removeEventListener(name, invalidate));
    window.removeEventListener('design_data_refresh_requested', requestRefresh);
  };

  wiredProjects.set(projectId, { count: 1, cleanup });

  return () => {
    const current = wiredProjects.get(projectId);

    if (!current) return;
    current.count -= 1;
    if (current.count > 0) return;
    current.cleanup();
    wiredProjects.delete(projectId);
  };
}
